// @MX:ANCHOR: 홈페이지 에디터 Vuex 모듈 (fan_in >= 3: HomepageEditor, PageSelector, EditorToolbar)
// @MX:REASON: HomepageEditor, PageSelector, EditorToolbar 컴포넌트 및 하위 섹션 편집기에서 공통으로 사용

// 섹션 순서 기본값 상수
const DEFAULT_SECTION_ORDER = [
  'hero', 'features', 'stats', 'partners', 'press', 'cta',
];

// localStorage 백업 키 (최신 1개만 유지)
const BACKUP_STORAGE_KEY = 'homepage-editor-backup';

// 백업 유효 기간: 24시간 (밀리초)
const BACKUP_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// 상태 초기값
const state = {
  currentPage: 'main', // 'main' | 'pricing' | 'services' | 'downloads'
  saveStatus: 'idle',  // 'idle' | 'saving' | 'saved' | 'error'
  pageData: {
    main: null,      // { sections: [], sectionOrder: [...], press: [] }
    pricing: null,   // PricingPlan[]
    services: null,  // { [slug]: ServicePage }
    downloads: null, // DownloadApp[]
  },
  originalSnapshot: null, // 로드 시점의 deep clone (dirty 감지용)
  activeEditor: null,     // { type: 'drawer'|'modal', sectionKey: string } | null
  saveError: null,        // 마지막 저장 에러 메시지
  failedSections: [],     // 저장 실패한 섹션 목록 (partial save 지원)
  backupRestored: false,  // localStorage 백업 복구 여부 플래그
};

// 게터
const getters = {
  // 현재 pageData가 최초 로드 시점과 다른지 여부
  isDirty: (state) => {
    if (!state.originalSnapshot) return false;
    return JSON.stringify(state.pageData) !== JSON.stringify(state.originalSnapshot);
  },
  // 현재 선택된 페이지의 데이터
  currentPageData: (state) => state.pageData[state.currentPage],
  // 특정 페이지의 데이터 로드 여부 확인
  isPageLoaded: (state) => (page) => state.pageData[page] !== null,
};

// 뮤테이션
const mutations = {
  // 현재 편집 중인 페이지 변경
  SET_CURRENT_PAGE(state, page) {
    state.currentPage = page;
  },
  // 특정 페이지 데이터 설정
  SET_PAGE_DATA(state, { page, data }) {
    state.pageData[page] = data;
  },
  // 저장 상태 변경
  SET_SAVE_STATUS(state, status) {
    state.saveStatus = status;
  },
  // 현재 pageData를 스냅샷으로 저장 (dirty 감지 기준점)
  SNAPSHOT_ORIGINAL(state) {
    state.originalSnapshot = JSON.parse(JSON.stringify(state.pageData));
  },
  // 메인 페이지 특정 섹션 데이터 업데이트
  UPDATE_SECTION(state, { page, sectionKey, data }) {
    if (page === 'main' && state.pageData.main) {
      const idx = state.pageData.main.sections.findIndex(s => s.sectionKey === sectionKey);
      if (idx !== -1) {
        state.pageData.main.sections[idx].data = data;
      }
    }
  },
  // 메인 페이지 섹션 순서 업데이트
  UPDATE_SECTION_ORDER(state, order) {
    if (state.pageData.main) {
      state.pageData.main.sectionOrder = order;
    }
  },
  // 현재 활성화된 편집기 설정 (drawer 또는 modal)
  SET_ACTIVE_EDITOR(state, editor) {
    state.activeEditor = editor;
  },
  // 저장 완료 후 dirty 상태 초기화
  RESET_DIRTY(state) {
    state.originalSnapshot = JSON.parse(JSON.stringify(state.pageData));
  },
  // 저장 에러 메시지 설정
  SET_SAVE_ERROR(state, error) {
    state.saveError = error;
  },
  // 저장 실패 섹션 목록 설정
  SET_FAILED_SECTIONS(state, sections) {
    state.failedSections = sections;
  },
  // 저장 실패 섹션 목록 초기화
  CLEAR_FAILED_SECTIONS(state) {
    state.failedSections = [];
  },
  // 백업 복구 플래그 설정
  SET_BACKUP_RESTORED(state, value) {
    state.backupRestored = value;
  },
};

// 액션
const actions = {
  // 특정 페이지 데이터 로드 (이미 로드된 경우 스킵)
  async loadPageData({ commit, state }, page) {
    // 이미 로드된 경우 재요청하지 않음
    if (state.pageData[page] !== null) return;

    try {
      const { HomepageApi } = await import('../../views/HomepageManagement/api');
      let data;

      switch (page) {
        case 'main': {
          // 콘텐츠와 보도자료를 병렬로 요청
          const [contents, press] = await Promise.all([
            HomepageApi.getContents(),
            HomepageApi.getPressArticles(),
          ]);
          const sections = contents.data || [];
          const orderSection = sections.find(s => s.sectionKey === 'section-order');
          const sectionOrder = orderSection?.data?.sections || DEFAULT_SECTION_ORDER;
          data = {
            sections,
            sectionOrder,
            press: press.data?.items || press.data || [],
          };
          break;
        }
        case 'pricing': {
          const res = await HomepageApi.getPricingPlans();
          data = res.data?.items || res.data || [];
          break;
        }
        case 'services': {
          const res = await HomepageApi.getServicePages();
          data = res.data || [];
          break;
        }
        case 'downloads': {
          const res = await HomepageApi.getDownloadApps();
          data = res.data?.items || res.data || [];
          break;
        }
        default:
          throw new Error(`알 수 없는 페이지: ${page}`);
      }

      commit('SET_PAGE_DATA', { page, data });
      commit('SNAPSHOT_ORIGINAL');
    } catch (error) {
      console.error(`[HomepageEditor] ${page} 데이터 로드 실패:`, error);
      throw error;
    }
  },

  // @MX:ANCHOR: 저장 액션 - 섹션별 개별 저장 + 부분 성공/실패 분리
  // @MX:REASON: EditorToolbar, HomepageEditor (Ctrl+S) 양쪽에서 호출됨 (fan_in >= 2)
  async saveCurrentPage({ commit, state }) {
    commit('SET_SAVE_STATUS', 'saving');
    commit('SET_SAVE_ERROR', null);
    commit('CLEAR_FAILED_SECTIONS');

    // 저장 전 현재 pageData를 localStorage에 스냅샷 (안전망)
    try {
      const backupPayload = JSON.stringify({
        timestamp: Date.now(),
        pageData: state.pageData,
      });
      localStorage.setItem(BACKUP_STORAGE_KEY, backupPayload);
    } catch (e) {
      // localStorage 저장 실패는 무시 (시크릿 모드 등)
      console.warn('[HomepageEditor] 저장 전 스냅샷 백업 실패:', e.message);
    }

    try {
      const { HomepageApi } = await import('../../views/HomepageManagement/api');
      const page = state.currentPage;

      if (page === 'main' && state.pageData.main) {
        const sections = state.pageData.main.sections;

        // 섹션별 개별 저장 요청을 Promise.allSettled로 병렬 처리
        const results = await Promise.allSettled(
          sections.map(s =>
            HomepageApi.bulkSaveContents({
              items: [{
                sectionKey: s.sectionKey,
                data: s.data,
                isActive: s.isActive ?? true,
              }],
            }).then(() => ({ sectionKey: s.sectionKey, success: true }))
              .catch(err => ({ sectionKey: s.sectionKey, success: false, error: err }))
          )
        );

        // 성공/실패 섹션 분리
        const failed = [];
        results.forEach(result => {
          // allSettled 자체는 항상 fulfilled지만, 내부 catch에서 success 플래그로 판단
          if (result.status === 'fulfilled' && !result.value.success) {
            failed.push(result.value.sectionKey);
          } else if (result.status === 'rejected') {
            // 예상치 못한 rejection (네트워크 차단 등)
            console.error('[HomepageEditor] 예상치 못한 rejection:', result.reason);
          }
        });

        if (failed.length === 0) {
          // 전체 성공
          commit('SET_SAVE_STATUS', 'saved');
          commit('RESET_DIRTY');
          // 저장 성공 시 백업 삭제
          try { localStorage.removeItem(BACKUP_STORAGE_KEY); } catch (e) { /* 무시 */ }
          setTimeout(() => commit('SET_SAVE_STATUS', 'idle'), 2000);
        } else if (failed.length < sections.length) {
          // 부분 성공: 성공한 섹션은 originalSnapshot 업데이트 후 실패 섹션 기록
          commit('SET_FAILED_SECTIONS', failed);
          commit('SET_SAVE_STATUS', 'error');
          const msg = `${failed.length}개 섹션 저장 실패: ${failed.join(', ')}`;
          commit('SET_SAVE_ERROR', msg);
          console.warn('[HomepageEditor] 부분 저장 실패:', msg);
          throw new Error(msg);
        } else {
          // 완전 실패
          commit('SET_FAILED_SECTIONS', failed);
          commit('SET_SAVE_STATUS', 'error');
          const msg = '모든 섹션 저장에 실패했습니다. 네트워크 연결을 확인해 주세요.';
          commit('SET_SAVE_ERROR', msg);
          console.error('[HomepageEditor] 전체 저장 실패');
          throw new Error(msg);
        }
      } else {
        // pricing, services, downloads는 Phase 6에서 구현 (일괄 저장)
        commit('SET_SAVE_STATUS', 'saved');
        commit('RESET_DIRTY');
        setTimeout(() => commit('SET_SAVE_STATUS', 'idle'), 2000);
      }
    } catch (error) {
      // 최상위 catch: API import 실패 등 예외 케이스
      if (state.saveStatus !== 'error') {
        commit('SET_SAVE_STATUS', 'error');
        commit('SET_SAVE_ERROR', error.message || '저장 중 알 수 없는 오류가 발생했습니다.');
      }
      throw error;
    }
  },

  // @MX:NOTE: localStorage 백업 확인 및 복구 가능 여부 감지 (mounted에서 호출)
  async checkAndRestoreBackup({ commit }) {
    try {
      const raw = localStorage.getItem(BACKUP_STORAGE_KEY);
      if (!raw) return;

      const backup = JSON.parse(raw);
      const age = Date.now() - (backup.timestamp || 0);

      // 24시간 이내 백업만 유효
      if (age > BACKUP_MAX_AGE_MS) {
        console.log('[HomepageEditor] 백업이 24시간 초과하여 무시됨');
        localStorage.removeItem(BACKUP_STORAGE_KEY);
        return;
      }

      // 백업 발견 → 사용자 확인 대기 플래그 설정 (즉시 적용하지 않음)
      commit('SET_BACKUP_RESTORED', true);
      console.log('[HomepageEditor] 복구 가능한 백업 발견 (timestamp:', new Date(backup.timestamp).toLocaleString(), ')');
    } catch (e) {
      // localStorage 읽기 실패는 무시 (시크릿 모드 등)
      console.warn('[HomepageEditor] 백업 확인 실패:', e.message);
    }
  },

  // @MX:NOTE: 사용자가 [복구] 버튼 클릭 시 localStorage 백업을 pageData에 적용
  applyBackup({ commit }) {
    try {
      const raw = localStorage.getItem(BACKUP_STORAGE_KEY);
      if (!raw) return;

      const backup = JSON.parse(raw);
      if (!backup.pageData) return;

      Object.keys(backup.pageData).forEach(page => {
        if (backup.pageData[page] !== null) {
          commit('SET_PAGE_DATA', { page, data: backup.pageData[page] });
        }
      });
      commit('SET_BACKUP_RESTORED', false);
      console.log('[HomepageEditor] 백업 복구 완료');
    } catch (e) {
      console.warn('[HomepageEditor] 백업 적용 실패:', e.message);
    }
  },

  // @MX:NOTE: 사용자가 [무시] 버튼 클릭 시 localStorage 백업 삭제
  discardBackup({ commit }) {
    try {
      localStorage.removeItem(BACKUP_STORAGE_KEY);
      commit('SET_BACKUP_RESTORED', false);
      console.log('[HomepageEditor] 백업 무시 및 삭제됨');
    } catch (e) {
      console.warn('[HomepageEditor] 백업 삭제 실패:', e.message);
    }
  },

  // @MX:NOTE: failedSections 목록의 섹션만 재시도 (재시도 버튼 클릭 시 호출)
  async retryFailedSections({ commit, state }) {
    if (!state.failedSections.length) return;

    commit('SET_SAVE_STATUS', 'saving');
    try {
      const { HomepageApi } = await import('../../views/HomepageManagement/api');
      const page = state.currentPage;

      if (page !== 'main' || !state.pageData.main) return;

      const retryKeys = new Set(state.failedSections);
      const sectionsToRetry = state.pageData.main.sections.filter(s => retryKeys.has(s.sectionKey));

      const results = await Promise.allSettled(
        sectionsToRetry.map(s =>
          HomepageApi.bulkSaveContents({
            items: [{
              sectionKey: s.sectionKey,
              data: s.data,
              isActive: s.isActive ?? true,
            }],
          }).then(() => ({ sectionKey: s.sectionKey, success: true }))
            .catch(err => ({ sectionKey: s.sectionKey, success: false, error: err }))
        )
      );

      // 여전히 실패한 섹션 추출
      const stillFailed = results
        .filter(r => r.status === 'fulfilled' && !r.value.success)
        .map(r => r.value.sectionKey);

      if (stillFailed.length === 0) {
        // 재시도 전체 성공
        commit('CLEAR_FAILED_SECTIONS');
        commit('SET_SAVE_ERROR', null);
        commit('SET_SAVE_STATUS', 'saved');
        commit('RESET_DIRTY');
        try { localStorage.removeItem(BACKUP_STORAGE_KEY); } catch (e) { /* 무시 */ }
        setTimeout(() => commit('SET_SAVE_STATUS', 'idle'), 2000);
      } else {
        // 일부 여전히 실패
        commit('SET_FAILED_SECTIONS', stillFailed);
        commit('SET_SAVE_STATUS', 'error');
        const msg = `${stillFailed.length}개 섹션 재시도 실패: ${stillFailed.join(', ')}`;
        commit('SET_SAVE_ERROR', msg);
        throw new Error(msg);
      }
    } catch (error) {
      if (state.saveStatus !== 'error') {
        commit('SET_SAVE_STATUS', 'error');
        commit('SET_SAVE_ERROR', error.message || '재시도 중 오류가 발생했습니다.');
      }
      throw error;
    }
  },

  // localStorage 백업 삭제 (레거시 clearBackup 대체 — 하위 호환 유지)
  clearBackup() {
    try {
      localStorage.removeItem(BACKUP_STORAGE_KEY);
      console.log('[HomepageEditor] localStorage 백업 삭제됨');
    } catch (e) {
      console.warn('[HomepageEditor] localStorage 백업 삭제 실패:', e.message);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
